/**
 * ============================================================================
 * CUSTOM SCRIPTS - MOSCOT THEME
 * ============================================================================
 * 
 * HOW TO ADD NEW FUNCTIONS:
 * 1. Add your function below this comment block
 * 2. Use clear function names (e.g., loadProductData)
 * 3. Add a brief comment above each function
 * 
 * THEME DEFAULT UTILITIES & EXAMPLES
 * ============================================================================
 * 
 * 1. Alpine.js Component Example:
 *    ----------------------------
 *    html: <div x-data="customData()"> ... </div>
 *    js:   function customData() {
 *            return {
 *              open: false,
 *              toggle() { this.open = !this.open }
 *            }
 *          }
 * 
 * 2. Alpine.js Global Store Example:
 *    -------------------------------
 *    js:   Alpine.store('myStore', {
 *            count: 0,
 *            add() { this.count++ }
 *          })
 *    html: <span x-text="$store.myStore.count"></span>
 * 
 * 3. Alpine.data Component Example:
 *    ------------------------------
 *    js:   Alpine.data('dropdown', (initialOpen = false) => ({
 *            open: initialOpen,
 *            init() {
 *               // Logic to run when component initializes
 *            },
 *            toggle() { this.open = !this.open }
 *          }))
 *    html: <div x-data="dropdown(true)"> ... </div>
 * 
 * Documentation: https://alpinejs.dev/directives/data
 * 
 * ============================================================================
 */

// Add your custom functions below this line

document.addEventListener('alpine:init', () => {
  const cachedResults = {};

  Alpine.data('lensBuilder', (
    BuilderPopupId,
    productUrl
    ) => ({
    popupId: BuilderPopupId,
    openBuilderPopup: false,
    contentLoaded: false,
    isLoading: false,
    init() {
      window.addEventListener('lens-builder-drawer:close-popup', () => {
        this.builderPopupRemoveFocus();
      });
    },
    async loadBuilderContent() {
      // Only load if not already loaded
      if (this.contentLoaded || this.isLoading) return;
      
      const cacheKey = productUrl;
      const contentContainer = this.builderPopup.querySelector('.js-builder-content');

      // Check cache first
      if (cachedResults[cacheKey] && contentContainer) {
        contentContainer.innerHTML = cachedResults[cacheKey];
        this.$nextTick(() => {
          if (window.Alpine) {
            window.Alpine.initTree(contentContainer);
          }
        });
        this.contentLoaded = true;
        return;
      }

      this.isLoading = true;
      
      try {
        const url = new URL(`${window.location.origin}${productUrl}`);
        url.searchParams.set('section_id', 'product-lens-builder');
        
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error('Failed to load content');
        
        const sectionHtml = await response.text();
        
        if (sectionHtml && contentContainer) {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sectionHtml;
          
          const shopifySection = tempDiv.querySelector('.shopify-section') || tempDiv.querySelector(`[id^='shopify-section']`);
          const htmlToCache = shopifySection ? shopifySection.innerHTML : sectionHtml;

          // Helper to load external resources (CSS/JS)
          const loadExternalResources = async (selector, tagName, attributes) => {
            const elements = tempDiv.querySelectorAll(selector);
            const promises = [];

            elements.forEach(el => {
              const srcOrHref = el.src || el.href;
              if (!srcOrHref) return;
              
              // Skip if already loaded or if it's a JSON script
              const baseUrl = srcOrHref.split('?')[0];
              if (document.querySelector(`${tagName}[${attributes.key}^="${baseUrl}"]`)) return;
              if (tagName === 'script' && el.getAttribute('type') === 'application/json') return;

              promises.push(new Promise((resolve, reject) => {
                const newEl = document.createElement(tagName);
                Object.entries(attributes).forEach(([key, value]) => newEl[key] = value || srcOrHref);
                newEl.onload = resolve;
                newEl.onerror = reject;
                document.head.appendChild(newEl);
              }));
              el.remove();
            });

            if (promises.length > 0) await Promise.all(promises);
          };

          // Load CSS first, then scripts
          await loadExternalResources('link[rel="stylesheet"]', 'link', { key: 'href', rel: 'stylesheet', href: null });
          await loadExternalResources('script[src]', 'script', { key: 'src', src: null, async: false });

          // NOW inject the HTML and let Alpine initialize
          contentContainer.innerHTML = htmlToCache;
          cachedResults[cacheKey] = htmlToCache;
          
          this.contentLoaded = true;
        }
      } catch (error) {
        console.error('Error loading lens builder content:', error);
      } finally {
        this.isLoading = false;
      }
    },
    
    builderPopupFocus() {
      Alpine.store('xFocusElement').trapFocus(this.popupId, 'CloseBuilderPopup');
    },
    
    builderPopupRemoveFocus() {
      const activeElement = document.getElementById('OpenBuilderPopup');
      if (activeElement) {
        Alpine.store('xFocusElement').removeTrapFocus(activeElement);
      }
      this.openBuilderPopup = false;
      Alpine.store('xPopup').close();
      window.dispatchEvent(new CustomEvent('eyeglass-builder:close-popup'));
    },

    open() {
      this.openBuilderPopup = true;
      Alpine.store('xPopup').open = true;
      this.builderPopup = document.getElementById(this.popupId);
      this.loadBuilderContent();
    }
  }));

  Alpine.data('yotpoPicturesWidget', (prevArrowHTML, nextArrowHTML) => ({
    init() {
      const container = this.$el;
      let attempts = 0;
      const maxAttempts = 40; // 20 seconds at 500ms

      const interval = setInterval(() => {
        attempts++;
        this.yotpoWrapper = container.querySelectorAll('.yotpo-display-wrapper');
        
        if (this.yotpoWrapper.length > 0) {
          this.yotpoWrapper.forEach(wrapper => {
            wrapper.classList.add('x-splide');
           if (!wrapper.classList.contains('custom-arrows-added')) {
             if (prevArrowHTML) wrapper.insertAdjacentHTML('beforeend', prevArrowHTML);
             if (nextArrowHTML) wrapper.insertAdjacentHTML('beforeend', nextArrowHTML);
             
             wrapper.classList.add('custom-arrows-added');
           }
          });
           clearInterval(interval);
        } else if (attempts >= maxAttempts) {
           clearInterval(interval);
        }
      }, 500);
    },
    makeSlide(slide){
      this.yotpoWrapper.forEach(wrapper => {
        const nextArrow = wrapper.querySelector('.arrows-wrapper-right .yotpo-icon-right-arrow-light');
        const prevArrow = wrapper.querySelector('.arrows-wrapper-left .yotpo-icon-left-arrow-light');
        if(nextArrow && slide == 1){
          nextArrow.click();
        }
        if(prevArrow && slide == 0){
          prevArrow.click();
        }
      })
    }
  }));

  /**
   * xBrowser Alpine Component
   * -------------------------
   * Detects the user's Operating System (OS) and Browser and applies corresponding CSS classes
   * to the element where this component is initialized.
   * 
   * Usage:
   * <div x-data="xBrowser"> ... </div>
   * 
   * Classes added:
   * - OS: 'windows', 'mac', 'android'
   * - Browser: 'isChrome', 'isFirefox', 'isSafari', 'isEdge', 'isIE', 'isOpera', 'isBlink'
   */
  Alpine.data('xBrowser', () => ({
    os: null,
    browser: null,

    init() {
      this.os = this.getOS();
      this.browser = this.getBrowser();
      this.applyClasses();
    },

    getOS() {
      const ua = navigator.userAgent;
      if (ua.includes("Android")) return "android";
      if (ua.includes("Mac")) return "mac";
      return "windows";
    },

    getBrowser() {
      const ua = navigator.userAgent;
      const isOpera = (!!window.opr && !!opr.addons) || !!window.opera || ua.indexOf(' OPR/') >= 0;
      const isFirefox = typeof InstallTrigger !== 'undefined';
      const isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && window['safari'].pushNotification));
      const isIE = /*@cc_on!@*/ false || !!document.documentMode;
      const isEdge = !isIE && !!window.StyleMedia;
      const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);
      const isEdgeChromium = isChrome && (ua.indexOf("Edg") !== -1);

      if (isFirefox) return 'isFirefox';
      if (isEdgeChromium) return 'isEdgeChromium';
      if (isChrome) return 'isChrome';
      if (isSafari) return 'isSafari';
      if (isOpera) return 'isOpera';
      if (isIE) return 'isIE';
      if (isEdge) return 'isEdge';
      return 'isBlink';
    },

    applyClasses() {
      if (this.os) this.$el.classList.add(this.os);
      if (this.browser) this.$el.classList.add(this.browser);
    }
  }));
});


